import { ORIGINAL_REPOSITORY_NAME_ANNOTATION } from '@internal/backstage-plugin-common-library-react';

import {
  Entity,
  EntityRelation,
  parseEntityRef,
} from '@backstage/catalog-model';
import { IdentityApi } from '@backstage/core-plugin-api';
import { CatalogApi } from '@backstage/plugin-catalog-react';

import { RevisionRequest } from './types';

export interface TeamOwnerInfo {
  teamId: string;
  teamName: string;
  teamEntityRef: string;
  manager: string;
  costCenter: string;
  costCenterId: string;
  ou: string;
  bu: string;
}

/**
 * Normalize a team reference to a full entity ref format.
 * Accepts: "sup0002448", "group:default/sup0002448", or "SUP0002448"
 * Returns: "group:default/sup0002448"
 */
export function normalizeTeamEntityRef(teamRefOrId: string): string {
  if (teamRefOrId.startsWith('group:')) {
    return teamRefOrId.toLowerCase();
  }
  return `group:default/${teamRefOrId.toLowerCase()}`;
}

/**
 * Extract team ID from entity ref or return as-is if already an ID.
 */
export function extractTeamId(teamEntityRef: string): string {
  return teamEntityRef.replace('group:default/', '').toUpperCase();
}

/**
 * Extract repository name from github project slug.
 * e.g., "tomtom-internal/repo-name" -> "repo-name"
 */
export function extractRepoName(githubProjectSlug: string): string {
  return githubProjectSlug.split('/')[1] || githubProjectSlug;
}

/**
 * Fetch team owner information from the catalog.
 * Accepts either a full entity ref (group:default/sup0002448) or just team ID (sup0002448).
 */
export async function fetchTeamOwnerInfo(
  catalogApi: CatalogApi,
  teamRefOrId: string,
): Promise<TeamOwnerInfo> {
  const teamEntityRef = normalizeTeamEntityRef(teamRefOrId);
  const teamEntity = await catalogApi.getEntityByRef(teamEntityRef);

  const teamId = extractTeamId(teamEntityRef);
  const teamName =
    (teamEntity?.metadata?.title as string) ||
    teamEntity?.metadata?.name ||
    teamId;

  const annotations = teamEntity?.metadata?.annotations || {};

  return {
    teamId,
    teamName,
    teamEntityRef,
    manager: annotations['graph.microsoft.com/group-manager'] || '',
    costCenter: annotations['graph.microsoft.com/cost-center-name'] || '',
    costCenterId: annotations['graph.microsoft.com/cost-center-id'] || '',
    ou: annotations['graph.microsoft.com/organization-unit-name'] || '',
    bu: annotations['graph.microsoft.com/business-unit-name'] || '',
  };
}

/**
 * Get parent team entity ref from a team entity.
 */
export async function getParentTeamRef(
  catalogApi: CatalogApi,
  teamRefOrId: string,
): Promise<string | undefined> {
  const teamEntityRef = normalizeTeamEntityRef(teamRefOrId);
  const teamEntity = await catalogApi.getEntityByRef(teamEntityRef);
  return teamEntity?.spec?.parent as string | undefined;
}

/**
 * Validate that team has all required owner fields.
 * Returns array of missing field names, empty if all present.
 */
export function validateTeamOwnerFields(teamInfo: TeamOwnerInfo): string[] {
  const missingFields: string[] = [];
  if (!teamInfo.manager) missingFields.push('manager');
  if (!teamInfo.costCenter) missingFields.push('cost center');
  if (!teamInfo.ou) missingFields.push('organization unit');
  if (!teamInfo.bu) missingFields.push('business unit');
  return missingFields;
}

/**
 * Build a revision request from team info.
 */
export function buildRevisionRequest(
  repositoryName: string,
  userEmail: string,
  originalTeamId: string,
  teamInfo: TeamOwnerInfo,
  recommendationType: 'PROPOSE_TEAM' | 'MARK_UNKNOWN',
  comment?: string,
): RevisionRequest {
  return {
    repository_name: repositoryName,
    user_email: userEmail,
    original_proposed_team_id: originalTeamId,
    recommendation_type: recommendationType,
    recommended_team_id: teamInfo.teamId,
    recommended_team_name: teamInfo.teamName,
    owner_manager: teamInfo.manager,
    owner_cost_center: teamInfo.costCenter,
    owner_cost_center_id: teamInfo.costCenterId || undefined,
    owner_ou: teamInfo.ou,
    owner_bu: teamInfo.bu,
    comment,
  };
}

/**
 * Get user email from identity API.
 */
export async function fetchUserEmail(
  identityApi: IdentityApi,
  catalogApi: CatalogApi,
): Promise<string> {
  const identity = await identityApi.getBackstageIdentity();
  const userEntity = await catalogApi.getEntityByRef(identity.userEntityRef);
  interface UserProfile {
    email?: string;
  }
  return (userEntity?.spec?.profile as UserProfile)?.email || 'unknown';
}

export function extractGroupName(targetRef: string): string | null {
  try {
    return parseEntityRef(targetRef).name;
  } catch {
    return null;
  }
}

export function extractSupIds(groupNames: string[]): string[] {
  return groupNames
    .filter(name => name.toLowerCase().startsWith('sup'))
    .map(name => name.toUpperCase());
}

export function pluralizePrefix(prefix: string, count: number): string {
  return `${count} ${prefix}${count === 1 ? 'y' : 'ies'}`;
}

export async function fetchUserGroups(
  identityApi: IdentityApi,
  catalogApi: CatalogApi,
): Promise<{ groupNames: string[]; supIds: string[] }> {
  const { userEntityRef } = await identityApi.getBackstageIdentity();
  const userEntity = await catalogApi.getEntityByRef(userEntityRef);

  const groupNames =
    (userEntity?.relations
      ?.filter(
        (r: EntityRelation) =>
          r.type === 'memberOf' && r.targetRef.startsWith('group:'),
      )
      .map((r: EntityRelation) => extractGroupName(r.targetRef))
      .filter(Boolean) as string[]) || [];

  const supIds = extractSupIds(groupNames);

  return { groupNames, supIds };
}

export async function fetchGroupTitles(
  catalogApi: CatalogApi,
  groupNames: string[],
): Promise<Record<string, string>> {
  const groupTitles: Record<string, string> = {};

  await Promise.all(
    groupNames.map(async groupName => {
      try {
        const groupEntity = await catalogApi.getEntityByRef(
          `group:default/${groupName}`,
        );
        groupTitles[groupName.toLowerCase()] =
          groupEntity?.metadata.title || groupName;
      } catch {
        groupTitles[groupName.toLowerCase()] = groupName;
      }
    }),
  );

  return groupTitles;
}

export async function validateRepoExistsInCatalog(
  catalogApi: CatalogApi,
  repoName: string,
): Promise<Entity | undefined> {
  try {
    // Match against either the entity name (normal repos) or the original-name
    // annotation (edge-case repos whose name was sanitized during ingestion).
    // An array of filters is OR-ed by the catalog.
    const { items } = await catalogApi.getEntities({
      filter: [
        { kind: 'Repository', 'metadata.name': repoName },
        {
          kind: 'Repository',
          [`metadata.annotations.${ORIGINAL_REPOSITORY_NAME_ANNOTATION}`]:
            repoName,
        },
      ],
    });

    return items.length > 0 ? items[0] : undefined;
  } catch {
    return undefined;
  }
}
